import { PlanetPosition, HouseInfo, ZodiacSign, PlanetName, CompleteKundali } from '../types/kundali';
import { ZODIAC_SIGNS, ZODIAC_LORDS, NAKSHATRAS, DASHA_ORDER } from './vedicEngine';

// Vimshottari Dasha Years (Total = 120)
const TOTAL_VIMSHOTTARI_YEARS = 120;

// Planet sequence starting from Ketu
const KP_PLANET_ORDER: PlanetName[] = [
  'Ketu',
  'Venus',
  'Sun',
  'Moon',
  'Mars',
  'Rahu',
  'Jupiter',
  'Saturn',
  'Mercury'
];

const PLANET_YEARS_MAP: Record<PlanetName, number> = {
  Ketu: 7,
  Venus: 20,
  Sun: 6,
  Moon: 10,
  Mars: 7,
  Rahu: 18,
  Jupiter: 16,
  Saturn: 19,
  Mercury: 17,
  Ascendant: 0
};

export interface KPCuspInfo {
  houseNumber: number;
  degree: number;
  formattedDegree: string;
  sign: ZodiacSign;
  signLord: string;
  starLord: string;
  subLord: string;
  subSubLord: string;
  nakshatra: string;
  pada: number;
}

export interface KPPlanetInfo {
  planet: PlanetName;
  degree: number;
  formattedDegree: string;
  sign: ZodiacSign;
  signLord: string;
  starLord: string;
  subLord: string;
  subSubLord: string;
  nakshatra: string;
  pada: number;
  house: number;
  isRetrograde: boolean;
}

export interface KPHouseSignificators {
  houseNumber: number;
  levelA_PlanetsInStarOfOccupants: PlanetName[];
  levelB_OccupantPlanets: PlanetName[];
  levelC_PlanetsInStarOfHouseLord: PlanetName[];
  levelD_HouseLord: PlanetName[];
}

export interface KPRulingPlanets {
  dayLord: string;
  moonStarLord: string;
  moonSignLord: string;
  ascendantStarLord: string;
  ascendantSignLord: string;
  nodeSubstitutes: { node: 'Rahu' | 'Ketu'; represents: string[] }[];
}

export interface KPAnalysisResult {
  cusps: KPCuspInfo[];
  planets: KPPlanetInfo[];
  significators: KPHouseSignificators[];
  rulingPlanets: KPRulingPlanets;
  horaryTable249: { number: number; sign: ZodiacSign; signLord: string; starLord: string; subLord: string; startDeg: number; endDeg: number }[];
}

/**
 * Calculates Star Lord, Sub Lord, and Sub-Sub Lord for any sidereal longitude (0 - 360)
 */
export function getKPLordsForLongitude(longitude: number): {
  sign: ZodiacSign;
  signLord: string;
  nakshatra: string;
  nakshatraIndex: number;
  pada: number;
  starLord: PlanetName;
  subLord: PlanetName;
  subSubLord: PlanetName;
  degInSign: number;
} {
  const normDeg = ((longitude % 360) + 360) % 360;
  const signIndex = Math.floor(normDeg / 30);
  const sign = ZODIAC_SIGNS[signIndex];
  const signLord = ZODIAC_LORDS[sign];
  const degInSign = normDeg % 30;

  // Nakshatra calculation (each 13°20' = 13.333333°)
  const nakshatraArc = 360 / 27; // 13.333333°
  const nakshatraIndex = Math.floor(normDeg / nakshatraArc);
  const nakshatraObj = NAKSHATRAS[nakshatraIndex % 27];
  const starLord = nakshatraObj.lord as PlanetName;
  const nakshatra = nakshatraObj.name;

  const degInNakshatra = normDeg % nakshatraArc;
  const pada = Math.floor(degInNakshatra / (nakshatraArc / 4)) + 1;

  // Sub Lord Calculation
  // 13°20' is divided into 9 segments proportional to Vimshottari Dasha years starting from star lord
  const starLordStartIndex = KP_PLANET_ORDER.indexOf(starLord);
  let accumulatedDeg = 0;
  let subLord: PlanetName = starLord;
  let subStartDeg = 0;
  let subArc = 0;

  for (let i = 0; i < 9; i++) {
    const currentPlanet = KP_PLANET_ORDER[(starLordStartIndex + i) % 9];
    const planetYears = PLANET_YEARS_MAP[currentPlanet];
    const currentSubArc = (planetYears / TOTAL_VIMSHOTTARI_YEARS) * nakshatraArc;

    if (degInNakshatra >= accumulatedDeg && degInNakshatra < accumulatedDeg + currentSubArc + 0.000001) {
      subLord = currentPlanet;
      subStartDeg = accumulatedDeg;
      subArc = currentSubArc;
      break;
    }
    accumulatedDeg += currentSubArc;
  }

  // Sub-Sub Lord Calculation
  // The sub arc is further divided into 9 segments proportional to Vimshottari Dasha years starting from sub lord
  const subLordStartIndex = KP_PLANET_ORDER.indexOf(subLord);
  const degInSub = degInNakshatra - subStartDeg;
  let subSubAccumulated = 0;
  let subSubLord: PlanetName = subLord;

  for (let j = 0; j < 9; j++) {
    const currentSubSubPlanet = KP_PLANET_ORDER[(subLordStartIndex + j) % 9];
    const planetYears = PLANET_YEARS_MAP[currentSubSubPlanet];
    const currentSubSubArc = (planetYears / TOTAL_VIMSHOTTARI_YEARS) * subArc;

    if (degInSub >= subSubAccumulated && degInSub < subSubAccumulated + currentSubSubArc + 0.000001) {
      subSubLord = currentSubSubPlanet;
      break;
    }
    subSubAccumulated += currentSubSubArc;
  }

  return {
    sign,
    signLord,
    nakshatra,
    nakshatraIndex,
    pada,
    starLord,
    subLord,
    subSubLord,
    degInSign
  };
}

/**
 * Generates the full KP 249 Sub-Divisions Table
 */
export function generateKP249Table(): {
  number: number;
  sign: ZodiacSign;
  signLord: string;
  starLord: string;
  subLord: string;
  startDeg: number;
  endDeg: number;
}[] {
  const table: {
    number: number;
    sign: ZodiacSign;
    signLord: string;
    starLord: string;
    subLord: string;
    startDeg: number;
    endDeg: number;
  }[] = [];

  let count = 1;
  const nakshatraArc = 360 / 27;

  for (let n = 0; n < 27; n++) {
    const nakObj = NAKSHATRAS[n];
    const starLord = nakObj.lord as PlanetName;
    const starLordIndex = KP_PLANET_ORDER.indexOf(starLord);
    const nakStart = n * nakshatraArc;

    let subOffset = 0;
    for (let s = 0; s < 9; s++) {
      const subPlanet = KP_PLANET_ORDER[(starLordIndex + s) % 9];
      const years = PLANET_YEARS_MAP[subPlanet];
      const subArc = (years / TOTAL_VIMSHOTTARI_YEARS) * nakshatraArc;

      const currentStart = nakStart + subOffset;
      const currentEnd = currentStart + subArc;

      const signIndex = Math.floor(currentStart / 30);
      const sign = ZODIAC_SIGNS[signIndex % 12];
      const signLord = ZODIAC_LORDS[sign];

      table.push({
        number: count++,
        sign,
        signLord,
        starLord,
        subLord: subPlanet,
        startDeg: currentStart,
        endDeg: currentEnd
      });

      subOffset += subArc;
    }
  }

  return table;
}

/**
 * Calculates complete KP System Astrological Profile for a given Kundali
 */
export function calculateKPProfile(kundali: CompleteKundali): KPAnalysisResult {
  const planets: KPPlanetInfo[] = kundali.planets.map((p) => {
    const kpData = getKPLordsForLongitude(p.longitude);
    const d = Math.floor(kpData.degInSign);
    const m = Math.floor((kpData.degInSign - d) * 60);
    const s = Math.round(((kpData.degInSign - d) * 60 - m) * 60);

    return {
      planet: p.name,
      degree: p.longitude,
      formattedDegree: `${d}° ${m}' ${s}"`,
      sign: kpData.sign,
      signLord: kpData.signLord,
      starLord: kpData.starLord,
      subLord: kpData.subLord,
      subSubLord: kpData.subSubLord,
      nakshatra: kpData.nakshatra,
      pada: kpData.pada,
      house: p.house,
      isRetrograde: !!p.isRetrograde
    };
  });

  // Calculate KP Cusps (Using Kundali House Mid-Points / Cusps)
  const cusps: KPCuspInfo[] = kundali.houses.map((h, idx) => {
    // If house startDegree is present, use it, else approximate from house sign & degree
    const signIdx = ZODIAC_SIGNS.indexOf(h.sign);
    const houseStartDeg = h.degreeStart !== undefined ? h.degreeStart : signIdx * 30;
    const kpData = getKPLordsForLongitude(houseStartDeg);
    const d = Math.floor(kpData.degInSign);
    const m = Math.floor((kpData.degInSign - d) * 60);
    const s = Math.round(((kpData.degInSign - d) * 60 - m) * 60);

    return {
      houseNumber: h.houseNumber || idx + 1,
      degree: houseStartDeg,
      formattedDegree: `${d}° ${m}' ${s}"`,
      sign: kpData.sign,
      signLord: kpData.signLord,
      starLord: kpData.starLord,
      subLord: kpData.subLord,
      subSubLord: kpData.subSubLord,
      nakshatra: kpData.nakshatra,
      pada: kpData.pada
    };
  });

  // Calculate 4-Step Significators (Levels A, B, C, D)
  const significators: KPHouseSignificators[] = [];

  for (let houseNum = 1; houseNum <= 12; houseNum++) {
    const occupantPlanets = planets.filter((p) => p.house === houseNum && p.planet !== 'Ascendant').map((p) => p.planet);
    const houseCusp = cusps.find((c) => c.houseNumber === houseNum);
    const houseLord = houseCusp ? (houseCusp.signLord as PlanetName) : ('Sun' as PlanetName);

    // Level A: Planets in the constellation (Star) of occupants of this house
    const levelA: PlanetName[] = [];
    occupantPlanets.forEach((occ) => {
      planets.forEach((p) => {
        if (p.starLord === occ && !levelA.includes(p.planet) && p.planet !== 'Ascendant') {
          levelA.push(p.planet);
        }
      });
    });

    // Level B: Planets occupying this house
    const levelB: PlanetName[] = [...occupantPlanets];

    // Level C: Planets in the constellation of the lord of this house
    const levelC: PlanetName[] = [];
    planets.forEach((p) => {
      if (p.starLord === houseLord && !levelC.includes(p.planet) && p.planet !== 'Ascendant') {
        levelC.push(p.planet);
      }
    });

    // Level D: The lord of this house
    const levelD: PlanetName[] = [houseLord];

    significators.push({
      houseNumber: houseNum,
      levelA_PlanetsInStarOfOccupants: levelA,
      levelB_OccupantPlanets: levelB,
      levelC_PlanetsInStarOfHouseLord: levelC,
      levelD_HouseLord: levelD
    });
  }

  // Calculate Ruling Planets (RP)
  const moonPlanet = planets.find((p) => p.planet === 'Moon');
  const ascPlanet = planets.find((p) => p.planet === 'Ascendant') || planets[0];

  // Day of week calculation
  const dayNames = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const birthDate = new Date(kundali.birthDetails.date);
  const dayOfWeek = isNaN(birthDate.getDay()) ? 0 : birthDate.getDay();
  const dayLord = dayNames[dayOfWeek];

  const rulingPlanets: KPRulingPlanets = {
    dayLord: dayLord,
    moonStarLord: moonPlanet?.starLord || 'Moon',
    moonSignLord: moonPlanet?.signLord || 'Moon',
    ascendantStarLord: ascPlanet?.starLord || 'Mars',
    ascendantSignLord: ascPlanet?.signLord || 'Mars',
    nodeSubstitutes: [
      {
        node: 'Rahu',
        represents: [
          planets.find((p) => p.planet === 'Rahu')?.signLord || 'Mercury',
          planets.find((p) => p.planet === 'Rahu')?.starLord || 'Mars'
        ]
      },
      {
        node: 'Ketu',
        represents: [
          planets.find((p) => p.planet === 'Ketu')?.signLord || 'Jupiter',
          planets.find((p) => p.planet === 'Ketu')?.starLord || 'Ketu'
        ]
      }
    ]
  };

  const horaryTable249 = generateKP249Table();

  return {
    cusps,
    planets,
    significators,
    rulingPlanets,
    horaryTable249
  };
}
